/**
 * @file htmlText.js
 * @description Utility for converting rich text HTML into plain text, used for
 * character counting and text previews across the notifications features.
 */

export const shouldAutoSyncChannel = ({ isChannelCustom, isAutoLinked }) =>
    !isChannelCustom && isAutoLinked;

const decodeHtmlEntities = str => {
    if (!str) return '';

    const namedEntities = {
        amp: '&',
        lt: '<',
        gt: '>',
        quot: '"',
        apos: "'",
        nbsp: '\u00A0',
    };

    return str.replace(/&(#x?[0-9a-fA-F]+|\w+);/g, (match, entity) => {
        if (entity[0] !== '#') return namedEntities[entity] || match;

        const isHex = entity[1].toLowerCase() === 'x';
        const codePoint = parseInt(
            entity.slice(isHex ? 2 : 1),
            isHex ? 16 : 10,
        );
        return Number.isNaN(codePoint)
            ? match
            : String.fromCodePoint(codePoint);
    });
};

/**
 * Strips unsupported rich-text features from notification content so it can be
 * safely reused in SMS or push drafts while preserving readable text.
 * @param {string} html - The HTML content to sanitize.
 * @param {string} channel - The target channel: sms or push.
 * @returns {string} The sanitized plain text content.
 */
export const sanitizeNotificationChannelText = (html, channel = 'sms') => {
    if (typeof html !== 'string' || !html.trim()) return '';

    const normalizedChannel = String(channel).toLowerCase();
    if (!['sms', 'push'].includes(normalizedChannel)) {
        return htmlToPlainText(html);
    }

    const root = new DOMParser().parseFromString(html, 'text/html').body;
    if (!root) return htmlToPlainText(html);

    root.querySelectorAll(
        'script, style, iframe, object, embed, svg, img, video, audio, source, track',
    ).forEach(element => element.remove());

    root.querySelectorAll('a, [data-portal-link="true"]').forEach(element => {
        const textContent = element.textContent || '';
        element.replaceWith(textContent);
    });

    const sanitizedText = htmlToPlainText(root.innerHTML);
    return sanitizedText
        .replace(/\u00A0/g, ' ')
        .replace(/\n\s*\n+/g, '\n')
        .trim();
};

/**
 * Converts rich text HTML content into a plain text representation,
 * collapsing block-level tags into newlines and trimming blank lines.
 * @param {string} html - The HTML content to convert.
 * @returns {string} The plain text representation of the given HTML.
 */
export const htmlToPlainText = html => {
    if (html == null) return '';

    let text = String(html);
    text = text.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p\s*>/gi, '\n');
    text = text.replace(/<p\b[^>]*>/gi, '');
    text = text.replace(/<\/div>\s*(?=<div\b)/gi, '\n');
    text = text.replace(/<\/div>/gi, '\n');
    text = text.replace(/<div\b[^>]*>/gi, '');
    text = text.replace(/<[^>]+>/g, '');
    text = decodeHtmlEntities(text);
    text = text.replace(/\r\n?/g, '\n');

    const collapsedLines = text
        .split('\n')
        .map(line => line.trim())
        .reduce((lines, line) => {
            const previousLineIsBlank = lines[lines.length - 1] === '';
            const skipBlankLine =
                line === '' && (lines.length === 0 || previousLineIsBlank);
            return skipBlankLine ? lines : [...lines, line];
        }, []);

    if (collapsedLines[0] === '') collapsedLines.shift();
    if (collapsedLines[collapsedLines.length - 1] === '') collapsedLines.pop();

    return collapsedLines.join('\n');
};
